import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { config } from 'utils/config';
import print from 'utils/print';
import { i18n } from 'utils/i18n';
import { sanitize } from 'utils/validation';
import { dtm } from 'utils/analytics';
import { usePrevious } from 'hooks/usePrevious';
import { ANALYTICS, MODAL, MODAL_THEMES } from 'constants';
import Modal from 'components/Modal';
import Button from 'components/Button';

/**
 * RentalTermsAndConditionsModal
 *
 * @param {object} props - React Props
 * @param {array} props.rentalTermsAndConditions
 * @param {function} props.getRentalTermsAndConditions
 * @return {JSX} - RentalTermsAndConditionsModal react component
 */

const RentalTermsAndConditionsModal = ({
  rentalTermsAndConditions,
  getRentalTermsAndConditions,
  isFranchisee,
  franchiseeName,
  isFetchingSession,
  gboReservationExists,
}) => {
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [termsByLang, setTermsByLang] = useState({});

  const prevRentalTermsAndConditions = usePrevious(rentalTermsAndConditions);
  const prevSelectedLanguage = usePrevious(selectedLanguage);
  const prevFetchingSession = usePrevious(isFetchingSession);

  const setLanguageContent = (rentalTC) => {
    const locale = config.getLocale();
    const updatedTermsByLang = { ...termsByLang };

    rentalTermsAndConditions?.forEach((term) => {
      updatedTermsByLang[term.locale] = updatedTermsByLang[term.locale] || term;
    });

    // first render to default to terms for domain locale
    if (!selectedLanguage) {
      const initialLanguage = updatedTermsByLang[locale] || rentalTermsAndConditions?.[0]; // if not found, we'll default the first in the arry
      setTermsByLang(updatedTermsByLang);
      setSelectedLanguage(initialLanguage);
    } else if (rentalTC && rentalTermsAndConditions) {
      // if rentalTC is updated
      const newSelectedLanguage = rentalTermsAndConditions?.[0] || updatedTermsByLang[locale];
      setTermsByLang(updatedTermsByLang);
      setSelectedLanguage(newSelectedLanguage);
    } else {
      setTermsByLang(updatedTermsByLang);
    }
  };

  useEffect(() => {
    if (rentalTermsAndConditions?.length) {
      setLanguageContent();
    } else {
      getRentalTermsAndConditions();
    }
  }, []);

  useEffect(() => {
    if (
      rentalTermsAndConditions?.length &&
      ((!prevRentalTermsAndConditions && !prevSelectedLanguage) ||
        rentalTermsAndConditions !== prevRentalTermsAndConditions)
    ) {
      setLanguageContent(rentalTermsAndConditions !== prevRentalTermsAndConditions);
    }
  }, [rentalTermsAndConditions, selectedLanguage]);

  // condition to wait for the response of the current session call `current` and
  // rentalTermsAndConditions` based on if `gbo.reservation` object is present or not.

  useEffect(() => {
    if (!rentalTermsAndConditions?.length && !isFetchingSession && prevFetchingSession && gboReservationExists) {
      getRentalTermsAndConditions();
    }
  }, [isFetchingSession, rentalTermsAndConditions, gboReservationExists]);

  const handleChange = (e) => {
    const code = e.target.value;
    const selected = termsByLang[code];
    setSelectedLanguage(selected);
  };

  const handlePrint = () => print([]);

  const getRentalTermsAndConditionsText = () => {
    let selectedContent = selectedLanguage?.rental_terms_and_conditions_text || '';

    if (selectedContent && isFranchisee && franchiseeName) {
      selectedContent = `<p>${i18n('rental_will_be_serviced_by', [franchiseeName])}</p>${selectedContent}`;
    }

    return selectedContent;
  };

  const selectedContent = getRentalTermsAndConditionsText();

  return (
    <Modal
      customClass='key-facts-modal key-facts-modal__rental-terms-conditions'
      modalKey={MODAL.KEY_RENTAL_FACTS_TERMS_AND_CONDITIONS}
      header={i18n('key_facts_modal_rental_terms_and_conditions_title')}
      theme={MODAL_THEMES.WHITE}
    >
      <div className='key-facts-modal__container key-facts-modal__container--terms-and-conditions'>
        <div className='key-facts-modal__actions-container'>
          {/* Language Toggle */}
          <div className='key-facts-modal__language-toggle__container'>
            <select
              className='key-facts-modal__language-toggle__select'
              aria-label={i18n('key_facts_modal_rental_terms_and_conditions_language_toggle')}
              onChange={handleChange}
              value='selected_language'
            >
              <option value={selectedLanguage?.locale}>{selectedLanguage?.locale_label}</option>
              {rentalTermsAndConditions &&
                rentalTermsAndConditions.map(
                  (option) =>
                    option?.locale !== selectedLanguage?.locale && (
                      <option key={option?.locale} value={option?.locale}>
                        {option?.locale_label}
                      </option>
                    )
                )}
            </select>
            <span id='selected_language' className='language-toggle__selected'>
              {selectedLanguage?.locale_label}
            </span>
          </div>

          {/* Print Button */}
          <Button
            link
            data-dtm-track={dtm(ANALYTICS.UI_BUTTON, ANALYTICS.TERMS_AND_CONDITIONS, ANALYTICS.PRINT)}
            className='key-facts-modal__print'
            onClick={handlePrint}
            ariaText={i18n('tab_content_print')}
          >
            <span>{i18n('tab_content_print')}</span>
          </Button>
        </div>

        {/* Content */}
        <div
          id={`${MODAL.KEY_RENTAL_FACTS_TERMS_AND_CONDITIONS}_desc`}
          className='key-facts-modal__content'
          dangerouslySetInnerHTML={sanitize(selectedContent)}
        />
      </div>
    </Modal>
  );
};

RentalTermsAndConditionsModal.propTypes = {
  rentalTermsAndConditions: PropTypes.array,
  getRentalTermsAndConditions: PropTypes.func,
  franchiseeName: PropTypes.string,
  isFranchisee: PropTypes.bool,
};

export default RentalTermsAndConditionsModal;
